import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import staticText from 'static/text_display.json';
import { HelpLinks } from 'utils/constants/helpLinks';
import {
  getSecurityCompanyServices,
  getSecurityCompanyId,
  getAllowableRoleServicesMap,
  getSecurityCompanyFeatures,
  allowableRolesTodisplay,
} from 'reducers/securityCompany';
import useFilters from 'containers/Base/useFilters.js';
import { getUserAssignedDepartmentList } from 'reducers/users';
import { t } from 'i18next';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import {
  personnelPageActions,
  personnelFilterActions,
  personnelGridActions,
  personnelSelectors,
} from '../';
import { getCurrentPage, detailOpenState } from 'reducers/ui';
import { PERSONNEL_ROLES } from 'utils/constants/roles';
import { PAGE_URLS } from 'utils/constants';
import { translatedKeyValueText } from 'utils/methods/helpers';

const Personnel = ({
  personnel,
  setFilters,
  filterValues,
  operationComponent,
  fetchPersonnelList,
  forceRefresh,
  setRefreshPersonnel,
  totalEntries,
  onEdit,
  FormComponent,
  toggleModal,
  editable,
  DetailComponent,
  currentView,
  subscribedServices,
  serviceRoleMap,
  enhancedOnSelectMap,
  defaultRoles,
  map,
  downloadable = true, //uploading and downloading of personnel CSV only shows on Managerial view
  uploadable = true,
  fetchPersonnelTemplateCSV,
  setPersonnelFilterKey,
  userAssignedDepartments,
  checkForRoles = true, // if filters doesnt require to send roles to the server, pass false
  availableRoles,
  subscribedFeatures,
  columns,
  filters,
  loading,
  uploadPersonnelCSV,
  securityCompanyId,
}) => {
  const rolesToDisplay = availableRoles;

  const [personnelGridAPI, setPersonnelGridAPI] = useState();

  const onMapSelectedPersonnelId = useSelector(state =>
    personnelSelectors.getSelectedRowID(state),
  );
  const detailOpen = useSelector(state => detailOpenState(state));
  const defaultFilters = {
    role: PERSONNEL_ROLES,
    service: '',
  };

  const { localFilterValues, updateFilter } = useFilters({
    filterValues: { ...filterValues, ...defaultFilters },
  });

  const onUpdate = filters => {
    fetchPersonnelList({ filters });
  };

  const fetchPersonnel = from => {
    if (
      (localFilterValues.role && localFilterValues.role.length > 0) ||
      !checkForRoles
    ) {
      fetchPersonnelList({
        filters: localFilterValues,
        from,
        pagination: true,
      });
    }
  };
  const cancelDetailPanel = () => {
    // removePersonnelId();
    // resetPersonnelDetail();
  };

  const gridAPIHandler = gridAPI => {
    setPersonnelGridAPI(gridAPI);
  };

  const handleEnhancedOnSelectMap = selectedData => {
    if (map && selectedData && selectedData.location) {
      map.panTo({
        lat: selectedData.location.latitude,
        lng: selectedData.location.longitude,
      });
    }
  };

  useEffect(() => {
    const personnelHighlight = onMapSelectedPersonnelId => {
      if (onMapSelectedPersonnelId != null) {
        const node =
          personnelGridAPI &&
          personnelGridAPI.getRowNode(onMapSelectedPersonnelId);
        // Go to the highlighted node
        personnelGridAPI &&
          personnelGridAPI.ensureNodeVisible(node, { position: 'top' });
        if (node) {
          if (!node.isSelected()) {
            node.setSelected(true);
          }
        }
      }
    };
    if (onMapSelectedPersonnelId) {
      personnelHighlight(onMapSelectedPersonnelId);
    }
  }, [onMapSelectedPersonnelId, personnelGridAPI]);

  const onDownloadClick = () => {
    fetchPersonnelTemplateCSV();
  };

  const documentUpload = e => {
    const { 0: file } = [...e.target.files]; //array destructuring to get selected csv file
    if (!file) {
      return;
    }
    uploadPersonnelCSV(file);
  };

  const onUploadClick = () => {
    //creates an input tag that allows the user to browse and select a personnel CSV doc to be uploaded
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.addEventListener('change', e => documentUpload(e));
    input.click();
  };

  const languageConvertedPersonnelList = personnel => {
    return personnel.map(m => {
      if (!m.id) {
        return {
          ...m,
          role: t(m.role),
        };
      } else {
        return {
          ...m,
          role: t(m.role),
        };
      }
    });
  };

  return (
    <LayoutWithMap
      onEdit={onEdit}
      loading={loading}
      toggleModal={toggleModal}
      FormComponent={FormComponent}
      header="Personnel"
      downloadHoverText={translatedKeyValueText('download_report_operational', {
        value: 'Personnel',
      })}
      uploadHoverText={translatedKeyValueText('upload_report_operational', {
        value: 'Personnel',
      })}
      cols={columns}
      editable={editable}
      DetailComponent={DetailComponent}
      filtersRequired={true}
      filters={filters({
        currentView,
        filterValues: localFilterValues,
        serviceRoleMap,
        subscribedServices,
        specificRoles: defaultRoles,
        userAssignedDepartments,
        availableRoles,
        subscribedFeatures,
        securityCompanyId,
      })}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: vals => {
            const personnelFilterKey = true;
            setPersonnelFilterKey(personnelFilterKey);
            setFilters(vals);
          },
        })
      }
      filterValues={localFilterValues}
      onUpdate={onUpdate}
      data={languageConvertedPersonnelList(personnel)}
      operationComponent={operationComponent}
      fetchData={fetchPersonnel}
      rowModelType="infinite"
      pagination={false}
      forceRefresh={forceRefresh}
      setForceRefresh={setRefreshPersonnel}
      totalEntries={totalEntries}
      createTextHover={staticText.managerial_view.personnel.create}
      formInfo={{
        link: HelpLinks.assignPatrolLink,
        value: HelpLinks.assignPatrolText,
      }}
      enhancedCancellation={cancelDetailPanel}
      setGridAPI={gridAPIHandler}
      defaultFilterValues={{
        role: rolesToDisplay.map(item => item.id),
        service: '',
      }}
      {...(enhancedOnSelectMap && {
        enhancedOnSelect: handleEnhancedOnSelectMap,
      })}
      downloadable={downloadable}
      {...(downloadable && {
        onDownloadClick: onDownloadClick,
        downloadHoverText:
          staticText.managerial_view.personnel.personnel_csv_download,
      })}
      uploadable={uploadable}
      {...(uploadable && {
        onUploadClick: onUploadClick,
        uploadHoverText:
          staticText.managerial_view.personnel.personnel_csv_upload,
      })}
      persistentStateName={PAGE_URLS.PERSONNEL.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.PERSONNEL.link]}
    />
  );
};

Personnel.propTypes = {
  personnel: PropTypes.array,
  columns: PropTypes.array,
  filters: PropTypes.func,
  setFilters: PropTypes.func,
  filterValues: PropTypes.object,
  filtersRequired: PropTypes.bool,
  loading: PropTypes.bool,
  onUpdate: PropTypes.func,
  operationComponent: PropTypes.object,
  fetchPersonnelList: PropTypes.func,
  forceRefresh: PropTypes.bool,
  totalEntries: PropTypes.number,
  onEdit: PropTypes.bool,
  FormComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  toggleModal: PropTypes.func,
  editable: PropTypes.bool,
  DetailComponent: PropTypes.func,
  setRefreshPersonnel: PropTypes.func,
  currentView: PropTypes.string,
  securityCompanyId: PropTypes.string,
  subscribedServices: PropTypes.array,
  resetPersonnelDetail: PropTypes.func,
  setGridAPI: PropTypes.func,
  allowableRoles: PropTypes.array,
  allowableServices: PropTypes.array,
  subscribedProducts: PropTypes.array,
  defaultRoles: PropTypes.array,
  map: PropTypes.object,
  enhancedOnSelectMap: PropTypes.bool,
  checkForRoles: PropTypes.bool,
  downloadable: PropTypes.bool,
  uploadable: PropTypes.bool,
  fetchPersonnelTemplateCSV: PropTypes.func,
  uploadPersonnelCSV: PropTypes.func,
  removeOnMapPersonnel: PropTypes.func,
  setPersonnelFilterKey: PropTypes.func,
  userAssignedDepartments: PropTypes.array,
  setPersonnelList: PropTypes.func,
  availableRoles: PropTypes.array,
  serviceRoleMap: PropTypes.array,
  subscribedFeatures: PropTypes.array,
};

Personnel.defaultProps = {
  editable: false,
};

const mapStateToProps = state => ({
  filterValues: personnelSelectors.getPersonnelFilters(state),
  totalEntries: personnelSelectors.getTotalRowsCount(state),
  forceRefresh: personnelSelectors.getForceRefresh(state),
  subscribedServices: getSecurityCompanyServices(state),
  userAssignedDepartments: getUserAssignedDepartmentList(state),
  serviceRoleMap: getAllowableRoleServicesMap(state),
  availableRoles: allowableRolesTodisplay(state),
  subscribedFeatures: getSecurityCompanyFeatures(state),
  currentView: getCurrentPage(state),
  securityCompanyId: getSecurityCompanyId(state),
});

const mapDispatchToProps = dispatch => ({
  setFilters: (filters, location) =>
    dispatch(personnelFilterActions.setFilters(filters, location)),
  fetchPersonnelList: ({ filters, from, pagination }) =>
    dispatch(personnelGridActions.fetchGridList({ filters, from, pagination })),
  setRefreshPersonnel: val =>
    dispatch(personnelGridActions.setForceRefresh(val)),
  fetchPersonnelTemplateCSV: () =>
    dispatch(personnelPageActions.fetchPersonnelCSVTemplate()),
  setPersonnelFilterKey: key =>
    dispatch(personnelFilterActions.setFilterKey(key)),
  uploadPersonnelCSV: personnelDoc =>
    dispatch(personnelPageActions.uploadPersonnelCSV({ personnelDoc })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Personnel);
